import React, { useCallback, useMemo, useState } from 'react';
import PaginationTable from '../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import SearchInput from '../../../../../../ui/DataEntry/SearchInput/SearchInput';
import styles from './TiktokCollectionsTopPosts.module.css';
import { OrderByParam } from '../../../../../../Hooks/useReactTableSortToOrderBy';
import { TiktokAudioCollectionPostStats } from '@round/api';
import { Column } from 'react-table';
import AccountCell from '../../../../../../SharedComponents/TableComponents/AccountCell/AccountCell';
import {
    buildTiktokMusicUrl,
    buildTiktokPostUrl,
    buildTiktokUserUrl,
    displayOptionalNumericTableValue,
    formatDateObjShort,
    numberWithCommas,
} from '../../../../../../helpers';
import TruncatedTextCell from '../../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import TitleCell from '../../../../../../SharedComponents/TableComponents/TitleCell/TitleCell';
import Skeleton from '../../../../../../ui/Skeleton/Skeleton';

type TiktokCollectionsTopPostsProps = {
    rows: TiktokAudioCollectionPostStats[];
    count: number;
    loading: boolean;
    errorLoading: boolean;
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    setPageSize: (pageSize: number | undefined) => void;
    search: string;
    setSearch: (query: string) => void;
    orderBy: OrderByParam<TiktokAudioCollectionPostStats>;
    setOrderBy: (orderBy: OrderByParam<TiktokAudioCollectionPostStats>) => void;
};

const TiktokCollectionsTopPosts = ({
    loading,
    errorLoading,
    count,
    rows,
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    setPageSize,
    orderBy,
    setOrderBy,
}: TiktokCollectionsTopPostsProps) => {
    const [tiktokTopPostsRowsDisplayed, setTiktokTopPostsRowsDisplayed] = useState(0);
    const topPostsRecordsShowingLabel = `displaying ${tiktokTopPostsRowsDisplayed} post${
        tiktokTopPostsRowsDisplayed === 1 ? '' : 's'
    } out of ${numberWithCommas(count)} analysed`;

    const columns = useMemo<Column<TiktokAudioCollectionPostStats>[]>(
        () => [
            {
                Header: 'Account',
                accessor: 'author_username',
                disableSortBy: true,
                Cell: (data) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <AccountCell
                            accountName={data.row.original.author_name}
                            accountUsername={data.value}
                            accountUrl={buildTiktokUserUrl(data.value)}
                        />
                    );
                },
            },
            {
                Header: 'Followers',
                accessor: 'author_follower_count',
                disableSortBy: true,
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
            {
                Header: 'Video Caption',
                accessor: 'caption',
                disableSortBy: true,
                Cell: ({ value }) => <>{loading ? <Skeleton /> : <TruncatedTextCell>{value}</TruncatedTextCell>}</>,
            },
            {
                Header: 'Song',
                accessor: 'audio_title',
                disableSortBy: true,
                Cell: (data) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <a
                            onClick={(e) => e.stopPropagation()}
                            className={styles.link}
                            href={buildTiktokMusicUrl(data.row.original.audio_title, data.row.original.audio_tiktok_id)}
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            <TitleCell title={data.value} subtitle={data.row.original.audio_author_name} />
                        </a>
                    );
                },
            },
            {
                Header: 'Audio',
                accessor: 'audio_alternate_name',
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    if (!original.audio_is_original) {
                        return <>Official Audio</>;
                    }

                    return <>{original.audio_alternate_name.trim() || 'Original Audio'}</>;
                },
            },
            {
                Header: 'Date Posted',
                accessor: 'create_time',
                Cell: (data) => (
                    <>{loading ? <Skeleton /> : formatDateObjShort(new Date(data.row.original.create_time * 1000))}</>
                ),
            },
            {
                Header: 'Plays',
                accessor: 'play_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
            {
                Header: 'Shares',
                accessor: 'share_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
            {
                Header: 'Saves',
                accessor: 'save_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
        ],
        [loading]
    );

    const noDataLabel = useMemo(() => {
        return errorLoading ? 'Sorry, we are experiencing technical issues' : 'No data available';
    }, [errorLoading]);

    const navigateToTikTokPost = useCallback((item: TiktokAudioCollectionPostStats) => {
        window.open(buildTiktokPostUrl(item.author_username, item.post_tiktok_id), '_blank', 'noopener noreferrer');
    }, []);

    return (
        <>
            <div className={styles.titles}>
                <p className={styles.title}>Top Posts</p>
                <p className={styles.subtitle}>{loading ? <Skeleton width="20rem" /> : topPostsRecordsShowingLabel}</p>
            </div>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <SearchInput value={search} onChange={setSearch} />
                </div>
            </div>
            <PaginationTable
                disableSortBy={false}
                manualSortBy
                manualPagination
                noDataLabel={noDataLabel}
                loading={loading}
                data={rows}
                columns={columns}
                count={count}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                orderBy={orderBy}
                onOrderByChange={setOrderBy}
                onRowClick={navigateToTikTokPost}
                tableClassName={styles.table}
                onPaginationChange={(state) => setTiktokTopPostsRowsDisplayed(state.pageRows.length)}
            />
        </>
    );
};

export default TiktokCollectionsTopPosts;
